/**
 * The theme context and its hook, kept apart from the provider component.
 *
 * Not a stylistic split: React Fast Refresh only preserves state in a module
 * that exports components and nothing else. A file exporting both
 * `<ThemeProvider>` and `useTheme` silently loses component state on every
 * edit during development, which is exactly the kind of papercut that gets
 * blamed on the framework.
 */

import { createContext, useContext } from "react";

import type { ResolvedTheme, ThemePreference } from "./theme";

export interface ThemeContextValue {
  /** What the user chose. May be "system". */
  preference: ThemePreference;
  /** What is actually rendered. Never "system". */
  resolved: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) {
    // A clear message beats a null-dereference three components deep.
    throw new Error("useTheme must be used inside a <ThemeProvider>.");
  }
  return value;
}
